import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

/**
 * Centralized subscription status hook
 * This ensures all pages use the same cached subscription data
 * and updates are reflected immediately across the entire app
 */
export const useSubscriptionStatus = (userId?: string) => {
  const queryClient = useQueryClient();
  const [overrideStatus, setOverrideStatus] = useState<boolean | null>(null);

  // Query the database for subscription status
  const { data: dbStatus, isLoading } = useQuery({
    queryKey: ["subscription-status", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("is_premium, is_lifetime_premium, subscription_product_id, subscription_end")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // Keep data fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Listen for subscription sync events from SubscriptionSync component
  useEffect(() => {
    const handleSyncComplete = (event: CustomEvent) => {
      if (event.detail.userId === userId) {
        // Immediately update override status
        setOverrideStatus(event.detail.isPremium);
        // Invalidate query to refetch from database
        queryClient.invalidateQueries({ queryKey: ["subscription-status", userId] });
      }
    };

    window.addEventListener("subscription-synced" as any, handleSyncComplete);
    return () => {
      window.removeEventListener("subscription-synced" as any, handleSyncComplete);
    };
  }, [userId, queryClient]);

  // Use override status if available, otherwise fall back to database
  const isPremium = overrideStatus !== null 
    ? overrideStatus 
    : (dbStatus?.is_premium || dbStatus?.is_lifetime_premium || false);

  return {
    isPremium,
    isLifetime: dbStatus?.is_lifetime_premium || false,
    subscriptionProductId: dbStatus?.subscription_product_id,
    subscriptionEnd: dbStatus?.subscription_end,
    isLoading,
    // For backwards compatibility
    profile: dbStatus,
  };
};
